import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Divider,
  Alert,
  FormControlLabel,
  Autocomplete,
  Chip,
  Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import JiraIcon from '@mui/icons-material/BugReport';
import { createItem, api } from '../../utils/api';
import { useSession } from '../../context/SessionContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Link as RouterLink } from 'react-router-dom';

// Helper function to convert Atlassian Document Format (ADF) to plain text
const adfToPlainText = (adf) => {
  if (!adf) return '';
  if (typeof adf === 'string') return adf;

  // If it's an ADF object, extract text from content
  if (adf.type === 'doc' && adf.content) {
    const extractText = (nodes) => {
      if (!nodes || !Array.isArray(nodes)) return '';

      return nodes.map(node => {
        if (node.type === 'text') {
          return node.text || '';
        }
        if (node.content) {
          return extractText(node.content);
        }
        // Add line breaks for block elements
        if (['paragraph', 'heading', 'bulletList', 'orderedList', 'listItem'].includes(node.type)) {
          return extractText(node.content) + '\n';
        }
        return '';
      }).join('');
    };

    return extractText(adf.content).trim();
  }

  // Fallback: try to stringify if it's some other object
  return typeof adf === 'object' ? JSON.stringify(adf) : String(adf);
};

const JiraImport = ({ sessionId, onItemsImported, open: externalOpen, onClose: externalOnClose }) => {
  const { addMultipleItems, fetchItems } = useSession();
  const { currentOrganization } = useWorkspace();
  const organizationId = currentOrganization?.id || null;

  // Support both internal state and external control
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const [projectKey, setProjectKey] = useState('');
  const [storyPointFieldId, setStoryPointFieldId] = useState('');
  const [issueType, setIssueType] = useState('');
  const [excludeEstimated, setExcludeEstimated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jiraItems, setJiraItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [error, setError] = useState(null);

  // Total results tracking
  const [totalResults, setTotalResults] = useState(0);

  // Story ID search
  const [storyIdSearch, setStoryIdSearch] = useState('');
  const [foundStory, setFoundStory] = useState(null);
  const [loadingStorySearch, setLoadingStorySearch] = useState(false);

  // Keyword search
  const [keywordSearch, setKeywordSearch] = useState('');

  // Epic filtering state
  const [epics, setEpics] = useState([]);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [loadingEpics, setLoadingEpics] = useState(false);
  const [expandedEpics, setExpandedEpics] = useState({});
  
  const [userJiraSettings, setUserJiraSettings] = useState({
    isJiraConfigured: false,
    jiraUrl: '',
    jiraUsername: '',
    defaultProjectKey: '',
    defaultStoryPointFieldId: ''
  });
  const [loadingUserSettings, setLoadingUserSettings] = useState(true);
  
  // Reset state when dialog opens (including external control)
  useEffect(() => {
    if (open) {
      setJiraItems([]);
      setSelectedItems([]);
      setError(null);
      setSelectedEpic(null);
      setExpandedEpics({});
      setTotalResults(0);
      setStoryIdSearch('');
      setFoundStory(null);
      setKeywordSearch('');
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setLoadingUserSettings(true);
      // Reset fields to ensure defaults are applied if user hasn't typed yet
      setProjectKey('');
      setStoryPointFieldId('');
      setIssueType('');

      // Check credentials source (user-level or org-level)
      const credentialsUrl = organizationId
        ? `/jira/credentials-source?organizationId=${organizationId}`
        : '/jira/credentials-source';

      Promise.all([
        api.get('/users/me/settings/jira'),
        api.get(credentialsUrl).catch(() => ({ data: { source: null, isConfigured: false } }))
      ]).then(([userSettingsResponse, credentialsResponse]) => {
        const {
          isJiraApiTokenSet,
          jiraUrl: userJiraUrl,
          jiraUsername: userJiraUsername,
          jiraProjectKey: userDefaultProjectKey,
          jiraStoryPointFieldId: userDefaultStoryPointFieldId
        } = userSettingsResponse.data;

        const credSource = credentialsResponse.data;

        // Use credentials source to determine if JIRA is available
        const isConfigured = credSource.isConfigured || (isJiraApiTokenSet && !!userJiraUrl);

        setUserJiraSettings({
          isJiraConfigured: isConfigured,
          jiraUrl: credSource.url || userJiraUrl || '',
          jiraUsername: credSource.username || userJiraUsername || '',
          defaultProjectKey: userDefaultProjectKey || '',
          defaultStoryPointFieldId: userDefaultStoryPointFieldId || '',
          credentialSource: credSource.source,
          organizationName: credSource.organizationName,
        });

        // Set projectKey from user settings if available
        if (userDefaultProjectKey) {
          setProjectKey(userDefaultProjectKey);
        }
        // Set storyPointFieldId from user settings if available
        if (userDefaultStoryPointFieldId) {
          setStoryPointFieldId(userDefaultStoryPointFieldId);
        }
        setError(null);
      }).catch(err => {
        console.error('Failed to fetch JIRA settings in JiraImport dialog:', err);
        setError('Could not load JIRA configuration. Please check settings.');
        setUserJiraSettings({ isJiraConfigured: false, jiraUrl: '', jiraUsername: '', defaultProjectKey: '', defaultStoryPointFieldId: '' });
      }).finally(() => {
        setLoadingUserSettings(false);
      });
    }
  }, [open, organizationId]);

  // Fetch epics when project key changes
  useEffect(() => {
    const fetchEpics = async () => {
      if (!projectKey || !userJiraSettings.isJiraConfigured || loadingUserSettings) {
        setEpics([]);
        return;
      }

      setLoadingEpics(true);
      try {
        const params = { projectKey };
        if (organizationId) params.organizationId = organizationId;
        const response = await api.get('/jira/epics', { params });
        setEpics(response.data.epics || []);
      } catch (err) {
        console.error('Error fetching epics:', err);
        setEpics([]);
      } finally {
        setLoadingEpics(false);
      }
    };

    // Debounce the fetch
    const timeoutId = setTimeout(fetchEpics, 500);
    return () => clearTimeout(timeoutId);
  }, [projectKey, userJiraSettings.isJiraConfigured, loadingUserSettings, organizationId]);

  // Group items by epic
  const groupedItems = useMemo(() => {
    if (jiraItems.length === 0) return {};

    const groups = {};
    jiraItems.forEach(item => {
      const parentKey = item.fields.parent?.key || null;
      const parentSummary = item.fields.parent?.fields?.summary || null;
      const groupKey = parentKey || '__ungrouped__';

      if (!groups[groupKey]) {
        groups[groupKey] = {
          epicKey: parentKey,
          epicSummary: parentSummary,
          items: []
        };
      }
      groups[groupKey].items.push(item);
    });

    return groups;
  }, [jiraItems]);

  // Initialize expanded state for all epic groups
  useEffect(() => {
    const newExpandedState = {};
    Object.keys(groupedItems).forEach(key => {
      newExpandedState[key] = true; // Default to expanded
    });
    setExpandedEpics(newExpandedState);
  }, [groupedItems]);

  // Toggle epic group expansion
  const toggleEpicExpanded = (epicKey) => {
    setExpandedEpics(prev => ({
      ...prev,
      [epicKey]: !prev[epicKey]
    }));
  };

  // Select/deselect all items in an epic group
  const handleToggleEpicGroup = (groupKey) => {
    const groupItemIds = groupedItems[groupKey]?.items.map(item => item.id) || [];
    const allSelected = groupItemIds.every(id => selectedItems.includes(id));

    if (allSelected) {
      // Deselect all items in this group
      setSelectedItems(prev => prev.filter(id => !groupItemIds.includes(id)));
    } else {
      // Select all items in this group
      setSelectedItems(prev => [...new Set([...prev, ...groupItemIds])]);
    }
  };

  // Check if all items in a group are selected
  const isGroupFullySelected = (groupKey) => {
    const groupItemIds = groupedItems[groupKey]?.items.map(item => item.id) || [];
    return groupItemIds.length > 0 && groupItemIds.every(id => selectedItems.includes(id));
  };

  // Check if some (but not all) items in a group are selected
  const isGroupPartiallySelected = (groupKey) => {
    const groupItemIds = groupedItems[groupKey]?.items.map(item => item.id) || [];
    const selectedCount = groupItemIds.filter(id => selectedItems.includes(id)).length;
    return selectedCount > 0 && selectedCount < groupItemIds.length;
  };
  
  const handleOpen = () => {
    if (externalOnClose) {
      // External control - just reset state, open is controlled externally
    } else {
      setInternalOpen(true);
    }
    setJiraItems([]);
    setSelectedItems([]);
    setError(null);
    setSelectedEpic(null);
    setExpandedEpics({});
    setTotalResults(0);
    setStoryIdSearch('');
    setFoundStory(null);
    setKeywordSearch('');
  };

  const handleClose = () => {
    if (externalOnClose) {
      externalOnClose();
    } else {
      setInternalOpen(false);
    }
  };

  const handleSearchByStoryId = async () => {
    if (!storyIdSearch.trim()) {
      setError('Please enter a Story ID to search');
      return;
    }

    if (!userJiraSettings.isJiraConfigured) {
      setError('JIRA is not configured. Please save your JIRA settings in the User Settings page.');
      return;
    }

    setLoadingStorySearch(true);
    setError(null);
    setFoundStory(null);

    try {
      const params = organizationId ? { organizationId } : {};
      const response = await api.get(`/jira/issue/${storyIdSearch.trim()}`, { params });
      const issue = response.data;

      // Detect if issue is an Epic or Initiative
      const isEpicType = ['Epic', 'Initiative'].includes(
        issue.fields?.issuetype?.name
      );

      setFoundStory({
        ...issue,
        isEpic: isEpicType
      });

      // Add the found story to jiraItems if not already present
      const existingIndex = jiraItems.findIndex(item => item.key === issue.key);
      if (existingIndex === -1) {
        const newIssue = {
          id: issue.id,
          key: issue.key,
          fields: issue.fields
        };
        setJiraItems(prev => [newIssue, ...prev]);
        // Pre-select the searched story
        setSelectedItems(prev => [...prev, issue.id]);
      } else {
        // Make sure it's selected
        if (!selectedItems.includes(issue.id)) {
          setSelectedItems(prev => [...prev, issue.id]);
        }
      }

      // If parent epic exists, update selectedEpic for context
      if (issue.parentEpic) {
        setSelectedEpic({
          key: issue.parentEpic.key,
          summary: issue.parentEpic.summary
        });
      }
    } catch (err) {
      console.error('Error searching for story:', err);
      setError(err.response?.data?.message || err.message || `Could not find story ${storyIdSearch.trim()}`);
    } finally {
      setLoadingStorySearch(false);
    }
  };

  const handleLoadEpicStories = async () => {
    if (!foundStory?.parentEpic?.key) {
      return;
    }

    const epicKey = foundStory.parentEpic.key;
    const projectKey = epicKey.split('-')[0];

    setLoading(true);
    setError(null);

    try {
      const payload = {
        projectKey: projectKey,
        issueType: issueType || undefined,
        excludeEstimated: excludeEstimated,
        storyPointFieldId: storyPointFieldId || undefined,
        epicKey: epicKey,
        organizationId: organizationId || undefined,
      };
      const response = await api.post('/jira/search', payload);

      const newIssues = response.data.issues || [];
      setTotalResults(response.data.total || 0);

      // Merge with existing items, keeping the original searched story selected
      const existingKeys = new Set(jiraItems.map(item => item.key));
      const uniqueNewIssues = newIssues.filter(issue => !existingKeys.has(issue.key));
      setJiraItems(prev => [...prev, ...uniqueNewIssues]);

      // Update project key field to match the epic's project
      if (!projectKey || projectKey !== epicKey.split('-')[0]) {
        setProjectKey(epicKey.split('-')[0]);
      }
    } catch (err) {
      console.error('Error loading epic stories:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to load stories from epic');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadChildStories = async () => {
    if (!foundStory?.isEpic) return;

    const epicKey = foundStory.key;
    const projectKeyFromEpic = epicKey.split('-')[0];

    setLoading(true);
    setError(null);

    try {
      const payload = {
        projectKey: projectKeyFromEpic,
        issueType: issueType || undefined,
        excludeEstimated,
        storyPointFieldId: storyPointFieldId || undefined,
        epicKey: epicKey,
        organizationId: organizationId || undefined,
      };
      const response = await api.post('/jira/search', payload);

      const newIssues = response.data.issues || [];
      setTotalResults(response.data.total || 0);

      // Merge with existing items
      const existingKeys = new Set(jiraItems.map(item => item.key));
      const uniqueNewIssues = newIssues.filter(issue => !existingKeys.has(issue.key));
      setJiraItems(prev => [...prev, ...uniqueNewIssues]);

      // Update project key field to match the epic's project
      if (!projectKey || projectKey !== projectKeyFromEpic) {
        setProjectKey(projectKeyFromEpic);
      }

      // Set the selectedEpic to this epic for context
      setSelectedEpic({
        key: epicKey,
        summary: foundStory.fields?.summary
      });
    } catch (err) {
      console.error('Error loading child stories:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to load child stories from epic');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (loadingUserSettings) {
      setError('JIRA configuration is still loading. Please wait.');
      return;
    }
    if (!userJiraSettings.isJiraConfigured) {
      setError('JIRA is not configured. Please save your JIRA settings in the User Settings page.');
      return;
    }
    if (!projectKey) {
      setError('Please enter a Project Key to search.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setJiraItems([]);
    setSelectedItems([]);
    setTotalResults(0);
    setFoundStory(null);
    
    try {
      const payload = {
        projectKey: projectKey,
        issueType: issueType || undefined,
        excludeEstimated: excludeEstimated,
        storyPointFieldId: storyPointFieldId || undefined,
        epicKey: selectedEpic?.key || undefined,
        keywords: keywordSearch || undefined,
        organizationId: organizationId || undefined,
      };
      const response = await api.post('/jira/search', payload);

      setJiraItems(response.data.issues || []);
      setTotalResults(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching Jira items:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to connect to Jira. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleItem = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };
  
  const handleImport = async () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one item to import');
      return;
    }
    
    setLoading(true);
    setError(null);
    const successfullyImportedItems = [];
    const failedItems = [];

    try {
      const itemsToProcess = jiraItems
        .filter(jiraItem => selectedItems.includes(jiraItem.id))
        .map(jiraItem => ({
          sessionId: sessionId,
          title: `${jiraItem.key}: ${jiraItem.fields.summary}`,
          description: adfToPlainText(jiraItem.fields.description),
          externalId: jiraItem.key,
          status: 'pending'
        }));

      for (const itemPayload of itemsToProcess) {
        try {
          const createdDbItem = await createItem(itemPayload);
          if (!createdDbItem || typeof createdDbItem.id === 'undefined') {
            console.error('Error importing individual JIRA item: createItem did not return expected data structure', itemPayload.title, createdDbItem);
            failedItems.push({ title: itemPayload.title, error: 'Invalid item data received from server' });
            continue;
          }

          const newItem = { 
            ...createdDbItem,
            id: createdDbItem.id,
            _id: createdDbItem._id || createdDbItem.id
          };
          successfullyImportedItems.push(newItem);
        } catch (err) {
          console.error('Error importing individual JIRA item:', itemPayload.title, err.response ? err.response.data : err.message);
          failedItems.push({ title: itemPayload.title, error: (err.response ? err.response.data.msg : err.message) || 'Unknown error' });
        }
      }

      if (failedItems.length > 0) {
        const errorMessages = failedItems.map(f => `${f.title}: ${f.error}`).join('\n');
        setError(`Some items failed to import:\n${errorMessages}`);
      } else if (successfullyImportedItems.length === 0 && selectedItems.length > 0) {
        setError('No items were imported successfully, though some were selected. Check server logs.');
      }

      if (successfullyImportedItems.length > 0) {
        if (typeof addMultipleItems === 'function') {
          addMultipleItems(successfullyImportedItems);
        } else {
          fetchItems();
        }
        
        if (onItemsImported) {
          onItemsImported(successfullyImportedItems); 
        }
      } else {
        if (onItemsImported) {
          onItemsImported(successfullyImportedItems); 
        }
      }

      if (failedItems.length === 0 || successfullyImportedItems.length > 0) {
        if (!error || (failedItems.length > 0 && successfullyImportedItems.length > 0)) {
             handleClose();
        }
      }

    } catch (err) {
      console.error('Error during JIRA import process:', err);
      setError(err.message || 'An unexpected error occurred during import.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      {/* Only show button when not externally controlled */}
      {externalOpen === undefined && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<JiraIcon />}
          onClick={handleOpen}
          sx={{ mt: 2 }}
        >
          Import from Jira
        </Button>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <JiraIcon sx={{ mr: 1 }} />
            Import Items from Jira
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {loadingUserSettings ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading JIRA Configuration...</Typography>
            </Box>
          ) : !userJiraSettings.isJiraConfigured ? (
            <Paper elevation={0} sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>JIRA Not Configured</Typography>
              <Typography sx={{ mb: 2 }}>
                Please configure your JIRA connection in User Settings to import issues.
              </Typography>
              <Button component={RouterLink} to="/settings" variant="contained" onClick={handleClose}>
                Go to User Settings
              </Button>
            </Paper>
          ) : (
            <>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Jira Search Parameters</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box component="form" noValidate>
                    {userJiraSettings.jiraUrl && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {userJiraSettings.credentialSource === 'organization'
                          ? `Using ${userJiraSettings.organizationName || 'organization'} JIRA: ${userJiraSettings.jiraUrl}`
                          : `Using your JIRA: ${userJiraSettings.jiraUrl} (as ${userJiraSettings.jiraUsername || 'N/A'})`
                        }
                      </Alert>
                    )}
                    <TextField
                      label="Project Key"
                      value={projectKey}
                      onChange={(e) => {
                        setProjectKey(e.target.value);
                        setSelectedEpic(null); // Reset epic when project changes
                      }}
                      fullWidth
                      required
                      margin="normal"
                      placeholder="e.g. PROJ (Required)"
                      disabled={loading}
                    />
                    <TextField
                      label="Keyword Search (Optional)"
                      value={keywordSearch}
                      onChange={(e) => setKeywordSearch(e.target.value)}
                      fullWidth
                      margin="normal"
                      placeholder="e.g., Activity Log"
                      helperText="Search issues by summary or description"
                      disabled={loading}
                    />
                    <Divider sx={{ my: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        OR Search by Story ID
                      </Typography>
                    </Divider>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        label="Search by Story ID (Optional)"
                        value={storyIdSearch}
                        onChange={(e) => setStoryIdSearch(e.target.value)}
                        fullWidth
                        margin="normal"
                        placeholder="e.g., CSU-2244"
                        helperText="Enter a story ID to find it and its parent epic"
                        disabled={loading || loadingStorySearch}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearchByStoryId();
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={handleSearchByStoryId}
                        disabled={loading || loadingStorySearch || !storyIdSearch.trim()}
                        sx={{ mt: 2, minWidth: 100 }}
                      >
                        {loadingStorySearch ? <CircularProgress size={20} /> : 'Find'}
                      </Button>
                    </Box>
                    {foundStory && (
                      <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Found {foundStory.isEpic ? 'Epic' : 'Story'}:
                        </Typography>
                        <Typography variant="body2">
                          <strong>{foundStory.key}:</strong> {foundStory.fields?.summary}
                        </Typography>
                        {foundStory.isEpic ? (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              This is an Epic. Load all child stories for import:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={handleLoadChildStories}
                                disabled={loading}
                              >
                                {loading ? <CircularProgress size={16} /> : 'Load Child Stories'}
                              </Button>
                            </Box>
                          </Box>
                        ) : foundStory.parentEpic && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Parent Epic:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Chip
                                label={`${foundStory.parentEpic.key}: ${foundStory.parentEpic.summary || 'Epic'}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Button
                                size="small"
                                variant="contained"
                                onClick={handleLoadEpicStories}
                                disabled={loading}
                              >
                                {loading ? <CircularProgress size={16} /> : 'Load Epic Stories'}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Paper>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Autocomplete
                      options={epics}
                      getOptionLabel={(option) => `${option.key}: ${option.summary}`}
                      value={selectedEpic}
                      onChange={(event, newValue) => setSelectedEpic(newValue)}
                      loading={loadingEpics}
                      disabled={loading || !projectKey}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Epic (Optional)"
                          margin="normal"
                          placeholder="Filter by Epic"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingEpics ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          helperText={projectKey && epics.length === 0 && !loadingEpics ? "No epics found or enter project key first" : "Select an epic to filter stories"}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" fontWeight="medium">
                              {option.key}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {option.summary}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      isOptionEqualToValue={(option, value) => option.key === value.key}
                      sx={{ mt: 1 }}
                    />
                    <TextField
                      label="Story Point Field ID (Optional)"
                      value={storyPointFieldId}
                      onChange={(e) => setStoryPointFieldId(e.target.value)}
                      fullWidth
                      margin="normal"
                      placeholder="e.g., customfield_10016"
                      disabled={loading}
                      helperText="Override default if needed for pushing estimates via other components."
                    />
                    <TextField
                      label="Issue Type (Optional)"
                      value={issueType}
                      onChange={(e) => setIssueType(e.target.value)}
                      fullWidth
                      margin="normal"
                      placeholder="e.g. Story"
                      disabled={loading}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={excludeEstimated}
                                onChange={(e) => setExcludeEstimated(e.target.checked)}
                                name="excludeEstimated"
                                color="primary"
                                disabled={loading}
                            />
                        }
                        label="Exclude items already estimated"
                        sx={{ mt: 1, mb: 1 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearch}
                      disabled={loading || !projectKey}
                      sx={{ mt: 2 }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Search Issues'}
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
          
              {jiraItems.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Found {jiraItems.length} of {totalResults} issues - Select items to import:
                  </Typography>
                  {totalResults > jiraItems.length && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Showing first {jiraItems.length} of {totalResults} results.
                      Use filters (Epic, Issue Type) to narrow your search.
                    </Alert>
                  )}
                  <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {Object.entries(groupedItems).map(([groupKey, group]) => (
                      <Box key={groupKey} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                        {/* Epic Group Header */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: '#141414',
                            px: 1,
                            py: 0.5,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#1a1a1a' }
                          }}
                          onClick={() => toggleEpicExpanded(groupKey)}
                        >
                          <Checkbox
                            checked={isGroupFullySelected(groupKey)}
                            indeterminate={isGroupPartiallySelected(groupKey)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleEpicGroup(groupKey);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            disabled={loading}
                            size="small"
                          />
                          <ExpandMoreIcon
                            sx={{
                              transform: expandedEpics[groupKey] ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s',
                              mr: 1
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            {group.epicKey ? (
                              <>
                                <Typography variant="body2" fontWeight="medium" component="span">
                                  {group.epicKey}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                                  {group.epicSummary}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2" fontWeight="medium" color="text.secondary">
                                Ungrouped (No Epic)
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={`${group.items.length} ${group.items.length === 1 ? 'item' : 'items'}`}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        </Box>

                        {/* Collapsible Items List */}
                        <Collapse in={expandedEpics[groupKey]}>
                          <List dense disablePadding>
                            {group.items.map((item) => (
                              <React.Fragment key={item.id}>
                                <ListItem sx={{ pl: 6 }}>
                                  <Checkbox
                                    edge="start"
                                    checked={selectedItems.includes(item.id)}
                                    onChange={() => handleToggleItem(item.id)}
                                    disabled={loading}
                                    size="small"
                                  />
                                  <ListItemText
                                    primary={`${item.key}: ${item.fields.summary}`}
                                    secondary={item.fields.issuetype?.name || 'Unknown Type'}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                  />
                                </ListItem>
                                <Divider component="li" />
                              </React.Fragment>
                            ))}
                          </List>
                        </Collapse>
                      </Box>
                    ))}
                  </Paper>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected {selectedItems.length} of {jiraItems.length} items
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            color="primary"
            variant="contained"
            disabled={loading || selectedItems.length === 0}
          >
            {loading ? <CircularProgress size={24} /> : `Import ${selectedItems.length} Items`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JiraImport; 